import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigation, useParsed } from "@refinedev/core";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { api, type KeywordMatchMode, type TrackedPost, type Trigger } from "../../api/client";
import { renderTemplate } from "../../lib/template";

const MATCH_MODES: { value: KeywordMatchMode; label: string }[] = [
  { value: "Contains", label: "Содержит (в любом месте)" },
  { value: "WholeWord", label: "Отдельное слово" },
];

export const TrackedPostEdit = () => {
  const { id } = useParsed();
  const { list } = useNavigation();
  const { message } = App.useApp();

  const [post, setPost] = useState<TrackedPost | null>(null);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    const [postResult, triggerResult] = await Promise.all([
      api.GET("/api/tracked-posts/{id}", { params: { path: { id: String(id) } } }),
      api.GET("/api/tracked-posts/{postId}/triggers", { params: { path: { postId: String(id) } } }),
    ]);

    setPost(postResult.data ?? null);
    setTriggers(triggerResult.data ?? []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const addTrigger = async () => {
    const { error } = await api.POST("/api/tracked-posts/{postId}/triggers", {
      params: { path: { postId: String(id) } },
      body: {
        name: "Новый триггер",
        keywords: ["ссылка"],
        matchMode: "Contains",
        publicReplyText: "Отправила в Direct 🙂",
        privateReplyMessage: "Держи ссылку: {newLine}{link}",
        link: "",
        isEnabled: true,
        priority: triggers.length,
      },
    });

    if (error) {
      message.error("Не удалось создать триггер");
      return;
    }

    await load();
  };

  if (loading) {
    return <Spin />;
  }

  if (!post) {
    return <Empty description="Задание не найдено" />;
  }

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={() => list("tracked-posts")}>← К списку заданий</Button>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space align="start">
          {post.thumbnailUrl && (
            <img
              src={post.thumbnailUrl}
              alt=""
              style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 6 }}
            />
          )}
          <Space direction="vertical" size={4}>
            <Typography.Text strong>{post.caption ?? post.mediaId}</Typography.Text>
            <Typography.Text type="secondary">Media ID: {post.mediaId}</Typography.Text>
            {post.permalink && (
              <Typography.Link href={post.permalink} target="_blank" rel="noreferrer">
                Открыть в Instagram
              </Typography.Link>
            )}
            <Space>
              <Switch
                checked={post.isEnabled}
                onChange={async (checked) => {
                  await api.PATCH("/api/tracked-posts/{id}", {
                    params: { path: { id: post.id } },
                    body: { isEnabled: checked },
                  });
                  await load();
                }}
              />
              <span>{post.isEnabled ? "Следим за комментариями" : "Отслеживание выключено"}</span>
            </Space>
          </Space>
        </Space>
      </Card>

      <Space style={{ marginBottom: 16 }} align="center">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Триггеры
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => void addTrigger()}>
          Добавить триггер
        </Button>
      </Space>

      {triggers.length === 0 ? (
        <Empty description="Пока нет триггеров. Бот не будет отвечать на комментарии под этим постом." />
      ) : (
        triggers.map((trigger) => (
          <TriggerCard key={trigger.id} trigger={trigger} onChanged={load} />
        ))
      )}
    </>
  );
};

const TriggerCard = ({ trigger, onChanged }: { trigger: Trigger; onChanged: () => Promise<void> }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<Trigger>();
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(
    renderTemplate(trigger.privateReplyMessage, trigger.link),
  );

  const save = async (values: Trigger) => {
    setSaving(true);

    const { error } = await api.PATCH("/api/triggers/{id}", {
      params: { path: { id: trigger.id } },
      body: {
        name: values.name,
        keywords: values.keywords,
        matchMode: values.matchMode,
        publicReplyText: values.publicReplyText ?? "",
        privateReplyMessage: values.privateReplyMessage,
        link: values.link ?? "",
        isEnabled: values.isEnabled,
        priority: values.priority,
      },
    });

    setSaving(false);

    if (error) {
      message.error("Не удалось сохранить триггер");
      return;
    }

    message.success("Сохранено");
    await onChanged();
  };

  const remove = async () => {
    await api.DELETE("/api/triggers/{id}", { params: { path: { id: trigger.id } } });
    await onChanged();
  };

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={trigger.name}
      extra={
        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => void remove()}>
          Удалить
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={trigger}
        onFinish={save}
        onValuesChange={(_, all) =>
          setPreview(renderTemplate(all.privateReplyMessage ?? "", all.link))
        }
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="name" label="Название" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item
              name="keywords"
              label="Ключевые слова"
              rules={[{ required: true, message: "Нужно хотя бы одно слово" }]}
              tooltip="Срабатывает, если найдено любое из слов. Enter — добавить."
            >
              <Select mode="tags" tokenSeparators={[","]} placeholder="ссылка, хочу, гайд" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="matchMode" label="Режим поиска">
              <Select options={MATCH_MODES} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="publicReplyText"
          label="Публичный ответ на комментарий"
          tooltip="Оставьте пустым, чтобы отвечать только в Direct."
        >
          <Input placeholder="Отправила в Direct 🙂" />
        </Form.Item>

        <Form.Item name="link" label="Ссылка">
          <Input placeholder="https://example.com/guide.pdf" />
        </Form.Item>

        <Form.Item
          name="privateReplyMessage"
          label="Сообщение в Direct"
          rules={[{ required: true }]}
          tooltip="Плейсхолдеры: {link}, {username}, {newLine}"
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          message="Как увидит подписчик"
          description={<pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{preview}</pre>}
        />

        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Form.Item
              name="priority"
              label="Приоритет"
              tooltip="Меньше — проверяется раньше. Срабатывает первый подошедший триггер."
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="isEnabled" label="Включён" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Button type="primary" htmlType="submit" loading={saving}>
          Сохранить
        </Button>
      </Form>
    </Card>
  );
};
